import {
  ACCESS_JWT_REFRESH_INTERVAL_MS,
  Auth,
  decodeToken,
  hasExpired,
  requireRoles
} from "@outschool/auth-shared";
import { ErrorMessages } from "@outschool/errors";
import { ModalType, OrganizationType } from "@outschool/gql-backend-generated";
import { Component } from "@outschool/ownership-areas";
import {
  addParamsToUrl,
  browseRootPath,
  outschoolForSchoolsUrl,
  pathFromLocation,
  removeParamsFromUrl
} from "@outschool/routes";
import * as Sentry from "@outschool/sentry/dist/client";
import * as Time from "@outschool/time";
import { Integration } from "@outschool/ui-analytics";
import { AuthTrigger, getAuthStrategy } from "@outschool/ui-auth";
import { sessionRefresh } from "@outschool/ui-gql/dist/apollo-v2";
import { UserFacingError, isApolloError } from "@outschool/ui-utils";
import ApolloClient from "apollo-client";
import * as H from "history";
import qs from "qs";

import { switchToLearnerModeWithToken } from "../../shared/client/useLearnerMode";
import * as Env from "../../shared/Env";
import * as Routes from "../../shared/Routes";
import ActionType from "../actions/ActionType";
import AppDispatcher from "../lib/AppDispatcher";
import { updateAttributionForCurrentUser } from "../lib/Attribution";
import {
  changeLearnerPasswordMutation,
  changePasswordMutation,
  loginMutation
} from "../queries/AuthenticationQueries";
import { currentUserQuery } from "../queries/CurrentUserQuery";
import BaseStore from "./BaseStore";

import type {
  AdSettings,
  AnalyticsInterface,
  SendIdentify,
  SendTrackEvent
} from "@outschool/ui-analytics";

declare global {
  interface Window {
    Intercom?: (action: string, options?: { [key: string]: unknown }) => void;
  }
}

export const SESSION_TOKEN_LOCAL_STORAGE_NAME = "jwt";
const APP_STATE_POST_LOGIN_ACTION_LOCAL_STORAGE_NAME =
  "APP_STATE_POST_LOGIN_ACTION";

/**
 * The single store that represents application state.
 */
export default class AppState extends BaseStore {
  appDispatcher: AppDispatcher;
  history: H.History;
  localStorage: undefined | Storage;
  apolloClient: ApolloClient<{}>;
  resetClientVersion: unknown;
  _getTokens:
    | undefined
    | (() => {
        sessionToken: string;
        refreshToken: string;
        decodedToken: Auth;
      });
  _setTokens: undefined | ((token: string, x: string) => unknown);
  _getAuthData:
    | undefined
    | (() => {
        isLoggedIn: boolean;
        isLeader: boolean;
        isAdmin: boolean;
        adminForOrganizationType: unknown;
      });
  _refreshTokenRepeaterTimer: ReturnType<typeof setTimeout>;
  _logoutCurrentUser: undefined | ((e?: unknown) => unknown);
  _currentUserHasLoaded: undefined | boolean;
  _setTopNotice: undefined | ((msg: string) => void);
  _setTimedTopNotice: undefined | ((msg: string) => void);
  changePasswordError: unknown;
  postLoginPath: unknown;
  postLoginAction: undefined | { actionType: string };
  intercomFailedToLoad: unknown;
  _updateUserInterfacePreferencesFromUser: undefined | ((x: unknown) => void);
  _currentUserIsLoading: undefined | boolean;
  currentUserHasAccount: undefined | boolean;
  _adSettings: undefined | AdSettings;
  _analytics: undefined | AnalyticsInterface;
  _identify: undefined | SendIdentify;
  _trackEvent: undefined | SendTrackEvent;

  /**
   * Initialize application state.
   */
  constructor(history, localStorage) {
    super();
    this.appDispatcher = new AppDispatcher();
    this.history = history;

    // Infrastructure
    this.subscribe(this.appDispatcher, () => this._handleAction.bind(this));
    this.localStorage = localStorage;
    this._topNoticeFailureHandler = this._topNoticeFailureHandler.bind(this);

    if (!Env.isTest) {
      console.log(`Client version is ${Env.clientVersion}`);
    }

    if (!Env.isNode) {
      this.appDispatcher.dispatch(ActionType.Webapp.CHECK_INTERCOM);
    }
  }

  setApolloClient(apolloClient) {
    this.apolloClient = apolloClient;
  }

  setResetClientVersion(resetClientVersion) {
    this.resetClientVersion = resetClientVersion;
  }

  setAuth({ getTokens, setTokens, getAuthData }) {
    this._getTokens = getTokens;
    this._setTokens = setTokens;
    this._getAuthData = getAuthData;
  }

  setLogoutCurrentUser(logoutCurrentUser) {
    this._logoutCurrentUser = logoutCurrentUser;
  }

  setAnalytics(
    analytics: AnalyticsInterface,
    identify: SendIdentify,
    trackEvent: SendTrackEvent,
    adSettings: AdSettings
  ) {
    this._analytics = analytics;
    this._identify = identify;
    this._trackEvent = trackEvent;
    this._adSettings = adSettings;
  }

  initUserData() {
    // User data.
    const sessionToken = this._getTokens().sessionToken;
    const refreshToken = this._getTokens().refreshToken;

    if (hasExpired(sessionToken) && hasExpired(refreshToken)) {
      this._logoutCurrentUser({
        reason: "initUserData: Both session and refresh token have expired"
      });
      this._currentUserHasLoaded = true;
    } else {
      if (sessionToken && window.location.pathname !== Routes.logoutPath()) {
        this.appDispatcher.dispatch(ActionType.User.REFRESH);
      } else if (!sessionToken) {
        this._currentUserHasLoaded = true;
      }
    }
  }

  /**
   * Action dispatch.
   * Invoked when the dispatcher sends an action our way.
   */
  async _handleAction(action) {
    if (this[action.actionType]) {
      return this[action.actionType](action);
    } else {
      console.log("Unknown action", action.actionType);
    }
  }

  setTopNoticeSetter(setTopNotice) {
    this._setTopNotice = setTopNotice;
  }

  setTimedTopNoticeSetter(setTimedTopNotice) {
    this._setTimedTopNotice = setTimedTopNotice;
  }

  _topNoticeFailureHandler(error) {
    if (Env.isDevelopment) {
      console.log("Top notice failure handler", error);
    }

    let message;
    if (isApolloError(error)) {
      message = new UserFacingError(error).toString();
    } else {
      message =
        error && error.message ? error.message : error && error.toString();
    }

    if (
      message === ErrorMessages.SESSION_HAS_EXPIRED ||
      message === ErrorMessages.USER_ACCOUNT_SUSPENDED ||
      message === ErrorMessages.USER_ACCOUNT_NOT_FOUND ||
      message === ErrorMessages.USER_ACCOUNT_LOCKED ||
      message === ErrorMessages.USER_ACCOUNT_LOCKED_INACTIVE ||
      message === ErrorMessages.INCORRECT_INFO ||
      message === ErrorMessages.CANT_REFRESH_TOKEN
    ) {
      console.log("Got Session has expired error. Logging out");
      this._logoutCurrentUser({
        reason: "Topnotice: Error from backend: " + message
      });
      this[ActionType.Webapp.ENTER_LOGIN_FLOW]({
        postLoginPath: window.location.pathname + window.location.search
      });
      this.history.push(Routes.loginPath());
      this._setTopNotice(
        "We have logged you out to protect your account. Please log back in to continue."
      );
      return;
    }

    this._setTopNotice(message);
    this.emitChange();
  }

  /****************************************************************************
   * ACTION HANDLERS
   *
   * Action handler methods, named after the actions themselves.
   */

  [ActionType.Webapp.WELCOME_AFTER_LOGIN](action) {
    const authTrigger = AuthTrigger.JOIN_OUTSCHOOL;
    if (!this._getAuthData().isLoggedIn) {
      const location = action.location;
      const path = location.pathname + location.search;

      this[ActionType.Webapp.ENTER_LOGIN_FLOW]({
        postLoginPath: path,
        postLoginAction: action
      });

      this.history.push(
        addParamsToUrl(pathFromLocation(action.location), {
          authTrigger,
          signup: true
        })
      );
    } else {
      this.setWelcomeModal({ authTrigger });
    }
  }

  [ActionType.SavedActivity.SAVE_AFTER_LOGIN](action) {
    const { loginStarted } = getAuthStrategy();
    const authTrigger = AuthTrigger.SAVE_CLASS;
    if (!this._getAuthData().isLoggedIn) {
      const location = action.location;
      const path = location.pathname + location.search;

      this[ActionType.Webapp.ENTER_LOGIN_FLOW]({
        postLoginPath: path,
        postLoginAction: action
      });
      this.history.push({
        pathname: location.pathname,
        query: {
          ...location.query,
          authTrigger,
          signup: true
        },
        state: { noScroll: true }
      });
    } else {
      if (this.localStorage && !loginStarted) {
        this.setWelcomeModal({
          authTrigger,
          flashMessage: `Your class was saved to your "Favorites"`
        });
        const { graphqlMutation } = action;
        this.apolloClient.mutate(graphqlMutation).then(() => {
          this.apolloClient.query({
            query: currentUserQuery,
            fetchPolicy: "network-only"
          });
        });
      }
    }
  }

  [ActionType.FollowedUser.FOLLOW_AFTER_LOGIN](action) {
    const authTrigger = AuthTrigger.FOLLOW_TEACHER;
    if (!this._getAuthData().isLoggedIn) {
      const { location } = action;
      this[ActionType.Webapp.ENTER_LOGIN_FLOW]({
        postLoginPath: location.pathname + location.search,
        postLoginAction: action
      });
      this.history.push({
        pathname: location.pathname,
        query: {
          ...location.query,
          authTrigger,
          signup: true
        },
        state: { noScroll: true }
      });
    } else {
      this.setWelcomeModal({
        authTrigger,
        flashMessage: `You are now following ${action.leaderName}`
      });
      const { graphqlMutation } = action;
      this.apolloClient.mutate(graphqlMutation).then(() => {
        this.apolloClient.query({
          query: currentUserQuery,
          fetchPolicy: "network-only"
        });
      });
    }
  }

  [ActionType.Webapp.MARK_HELPFUL_AFTER_LOGIN](action) {
    const authTrigger = AuthTrigger.MARK_REVIEW_HELPFUL;
    if (!this._getAuthData().isLoggedIn) {
      const location = action.location;
      this[ActionType.Webapp.ENTER_LOGIN_FLOW]({
        postLoginPath: location.pathname + location.search,
        postLoginAction: action
      });
      this.history.push({
        pathname: location.pathname,
        query: {
          ...location.query,
          authTrigger,
          signup: true
        },
        state: { noScroll: true }
      });
    } else {
      this.setWelcomeModal({
        authTrigger,
        flashMessage: `The review was marked as helpful`
      });
      const { graphqlMutation } = action;
      this.apolloClient.mutate(graphqlMutation);
    }
  }

  [ActionType.Webapp.SAVE_SEARCH_AFTER_LOGIN](action) {
    const authTrigger = AuthTrigger.SAVE_SEARCH;
    if (!this._getAuthData().isLoggedIn) {
      const location = action.location;
      this[ActionType.Webapp.ENTER_LOGIN_FLOW]({
        postLoginPath: location.pathname + location.search,
        postLoginAction: action
      });
      this.history.push({
        pathname: location.pathname,
        query: {
          ...location.query,
          authTrigger,
          signup: true
        },
        state: { noScroll: true }
      });
    } else {
      this.setWelcomeModal({
        authTrigger,
        flashMessage: `Your search was saved to your "Favorites"`
      });
      const { graphqlMutation } = action;
      this.apolloClient.mutate(graphqlMutation);
    }
  }

  // login flow used in outschool.com/teach
  async [ActionType.Webapp.TEACH_AFTER_LOGIN](action) {
    const { graphqlMutation, location } = action;
    // redirect to the teacher dashboard by default
    const postLoginPath = location?.pathname;
    if (!this._getAuthData().isLoggedIn) {
      this[ActionType.Webapp.ENTER_LOGIN_FLOW]({
        postLoginAction: action
      });

      // after successful login via the /teach page, redirect to /teach/dashboard
      this.history.push({
        pathname: postLoginPath,
        query: {
          ...location.query,
          authTrigger: AuthTrigger.JOIN_OUTSCHOOL,
          signup: true,
          useTeacherFlow: true
        },
        state: { noScroll: true }
      });
    } else {
      try {
        await this.apolloClient.mutate(graphqlMutation);
      } catch (e) {
        if (new UserFacingError(e).toString() !== "Already a teacher") {
          throw e;
        }
      }
      this.history.push(postLoginPath + location.search);
    }
  }

  // Params: userUid, token, newPassword
  async [ActionType.User.CHANGE_PASSWORD](action) {
    delete this.changePasswordError;

    try {
      const { data } = await this.apolloClient.mutate({
        mutation: action.userUid
          ? changePasswordMutation
          : changeLearnerPasswordMutation,
        variables: {
          uid: action.userUid || action.learnerUid,
          token: action.token,
          password: action.newPassword
        }
      });

      if (!action.userUid) {
        const { transferToken } = data.changeLearnerPassword;

        this._logoutCurrentUser({
          reason: "ChangePassword: Switching to learner mode"
        });
        switchToLearnerModeWithToken({
          transferToken,
          isLearnerLogin: true
        });
        return;
      }
      const { sessionToken } = data.changePassword;

      this._setTokens(sessionToken, null);
      this._trackEvent?.(ActionType.User.CHANGE_PASSWORD + "_COMPLETED");
      await this._refreshCurrentUser();
      this._setTimedTopNotice(
        "You have successfully changed your password. Thanks!"
      );
      this.history.push(Routes.accountPath(this._getAuthData().isLeader));
    } catch (e) {
      this._setTopNotice(new UserFacingError(e).toString());
      this.emitChange();
    }
  }

  // Params: email, password
  async [ActionType.User.LOGIN](action) {
    const { email, password, postLoginPath } = action;
    // set this.postLoginPath for redirectAfterLogin
    if (!!postLoginPath) {
      this.postLoginPath = postLoginPath;
    }

    try {
      const { data } = await this.apolloClient.mutate({
        mutation: loginMutation,
        variables: {
          emailOrUsername: email,
          password
        }
      });
      const decodedToken = decodeToken(data.login.sessionToken);
      //@ts-ignore-error Accessing an occasional property
      if (decodedToken.isLearnerTransfer) {
        this._logoutCurrentUser({ reason: "Login: Switching to learner mode" });
        switchToLearnerModeWithToken({
          transferToken: data.login.sessionToken,
          isLearnerLogin: true
        });
        return;
      }
      this._loginCurrentUser(data.login.sessionToken, data.login.refreshToken);
      this.emitChange();
      this._startRefreshSessionTokenRepeater();
      const currentUser = await this._refreshCurrentUser();
      const isSellerOrg = currentUser && currentUser.latestSellerOrgApplication;
      this.redirectAfterLogin(
        Routes.defaultPostLoginPath(
          currentUser,
          this._getAuthData().isLeader,
          isSellerOrg
        )
      );
    } catch (e) {
      this._setTopNotice(new UserFacingError(e).toString());
      this.emitChange();
    }
  }

  async [ActionType.User.LOGGED_IN](action) {
    this._loginCurrentUser(action.sessionToken);
    await this._refreshCurrentUser();
    this.emitChange();
    this._startRefreshSessionTokenRepeater();
  }

  async [ActionType.User.REFRESH]() {
    if (!this._getTokens().sessionToken) {
      this._currentUserHasLoaded = true;
      return;
    }

    try {
      await this._refreshSessionToken();
    } catch (e) {
      this._currentUserHasLoaded = true;
      this._topNoticeFailureHandler(e);
      return;
    }

    try {
      await this._refreshCurrentUser();
    } catch (e) {
      this._topNoticeFailureHandler(e);
      return;
    }

    if (this.localStorage) {
      try {
        const action = JSON.parse(
          this.localStorage.getItem(
            APP_STATE_POST_LOGIN_ACTION_LOCAL_STORAGE_NAME
          )
        );
        if (action) {
          this.postLoginAction = action;
          this.redirectAfterLogin();
        }
      } catch (e) {
        if (e instanceof SyntaxError) {
        } else {
          throw e;
        }
      }

      this.localStorage.removeItem(
        APP_STATE_POST_LOGIN_ACTION_LOCAL_STORAGE_NAME
      );
    }
    this.emitChange();
    this._startRefreshSessionTokenRepeater();
  }

  [ActionType.User.REDIRECT_TO_LEARNER_APP](action) {
    const { authRequirement, transferToken, learnerAppUrl, isLearnerLogin } =
      action;
    if (authRequirement) {
      this._logoutCurrentUser({
        reason: "RedirectToLearner: Switching to learner mode"
      });
    }
    switchToLearnerModeWithToken({
      transferToken,
      learnerAppUrl,
      isLearnerLogin
    });
  }

  async [ActionType.User.CURRENT_USER_UPDATED](action) {
    await this._updateCurrentUser(action.property, action?.value);
    this.emitChange();
  }

  [ActionType.Webapp.ENTER_LOGIN_FLOW](action) {
    this.postLoginAction = action.postLoginAction || this.postLoginAction;
    this.postLoginPath = action.postLoginPath;
    // No need to emit change here, save state before redirecting to login/signup
  }

  persistPostLoginAction() {
    if (this.localStorage) {
      if (this.postLoginAction) {
        this.localStorage.setItem(
          APP_STATE_POST_LOGIN_ACTION_LOCAL_STORAGE_NAME,
          JSON.stringify(this.postLoginAction)
        );
      }
    }
  }

  setWelcomeModal(props: { authTrigger: string; flashMessage?: string }) {
    this.apolloClient.writeData({
      data: {
        appState: {
          __typename: "AppState",
          openModal: ModalType.Welcome,
          authTrigger: props.authTrigger,
          welcomeFlashMessage: props.flashMessage
        }
      }
    });
  }

  [ActionType.Webapp.CHECK_INTERCOM]() {
    this.intercomFailedToLoad = true;
    setTimeout(() => {
      this.intercomFailedToLoad = !window?.Intercom;
      const searchParams = qs.parse(window.location.search, {
        ignoreQueryPrefix: true
      });
      if (searchParams["intercom"] === "true") {
        if (this.intercomFailedToLoad) {
          this.emitChange();
        } else {
          window?.Intercom?.("show");
        }
      }
    }, 5000);
  }

  redirectAfterLogin(defaultPath?) {
    const { loginStarted, loginRedirect } = getAuthStrategy();
    if (
      !this.postLoginAction &&
      this.localStorage &&
      this.localStorage.getItem(loginStarted)
    ) {
      try {
        this.postLoginAction = JSON.parse(
          this.localStorage.getItem(loginStarted)
        );
      } catch (e) {
        console.log("does not have a defined post login action");
      }
      this.localStorage.removeItem(loginStarted);
    }

    if (
      !this.postLoginPath &&
      this.localStorage &&
      this.localStorage.getItem(loginRedirect)
    ) {
      try {
        this.postLoginPath = JSON.parse(
          this.localStorage.getItem(loginRedirect)
        );
      } catch (e) {
        console.log("Did not have a post login path set");
      }
      this.localStorage.removeItem(loginRedirect);
    }

    if (this.postLoginAction) {
      this.appDispatcher.dispatch(
        this.postLoginAction.actionType,
        this.postLoginAction
      );
    }
    if (this.postLoginPath || defaultPath) {
      this.history.push(this.postLoginPath || defaultPath);
    }
    delete this.postLoginPath;
    delete this.postLoginAction;
  }

  /****************************************************************************
   * STATE ACCESSORS
   */
  // USERS
  setUpdateUserInterfacePreferencesFromUser(
    updateUserInterfacePreferencesFromUserFn
  ) {
    this._updateUserInterfacePreferencesFromUser =
      updateUserInterfacePreferencesFromUserFn;
  }

  _refreshSessionToken() {
    if (!Env.isTest) {
      console.log("Refresh Session Token");
    }

    const sessionToken = this._getTokens().sessionToken;
    const refreshToken = this._getTokens().refreshToken;
    if (
      hasExpired(sessionToken) &&
      (!refreshToken || hasExpired(refreshToken))
    ) {
      // When both tokens are 'expired', we _know_ that the user session has expired
      //Date.now() returns ms since the epoch and jwt returns seconds since the epoch
      const decodedSessionToken = decodeToken(sessionToken);
      const sessionExpiredSecs = Date.now() / 1000 - decodedSessionToken?.exp;
      const refreshExpiredSecs = refreshToken
        ? Date.now() / 1000 - decodeToken(refreshToken)?.exp
        : null;

      let loginProviders;
      if (decodedSessionToken && "providers" in decodedSessionToken) {
        loginProviders = decodedSessionToken.providers;
      }

      this._logoutCurrentUser({
        reason: "_refreshSessionToken: Both tokens have expired",
        sessionExpiredSecs,
        refreshExpiredSecs,
        loginProviders
      });
      return Promise.resolve(null);
    }

    if (!this._getTokens().refreshToken) {
      if (Env.ENABLED_FEATURE_FLAGS.includes("ppp-refresh-with-only-session")) {
        Sentry.logMessage("User refreshing session with only session token", {
          tags: {
            implementationLocation: "Appstate._refreshSessionToken",
            component: Component.UserAccountManagement
          }
        });
      } else {
        return Promise.resolve(null);
      }
    }
    return sessionRefresh(this.apolloClient, {
      refreshToken: this._getTokens().refreshToken,
      browserTimeZone: Time.guessBrowserTimeZone()
    }).then(({ data: { refreshV2 } }) => {
      this._setTokens(refreshV2.sessionToken, refreshV2.refreshToken);
    });
  }

  _startRefreshSessionTokenRepeater() {
    if (Env.isTest || !!Env.REFRESH_SESSION_TOKEN_REPEATER_DISABLED) {
      return;
    }
    const getTokens = this._getTokens;
    if (this._refreshTokenRepeaterTimer) {
      clearTimeout(this._refreshTokenRepeaterTimer);
    }
    this._refreshTokenRepeaterTimer = setTimeout(() => {
      this._refreshTokenRepeaterTimer = null;
      if (!getTokens().decodedToken) {
        return;
      }
      this._refreshSessionToken().then(() => {
        this._startRefreshSessionTokenRepeater();
      }, this._topNoticeFailureHandler);
    }, ACCESS_JWT_REFRESH_INTERVAL_MS);
  }

  async _refreshCurrentUser() {
    this._currentUserIsLoading = true;
    this.emitChange();

    try {
      const { data } = await this.apolloClient.query({
        query: currentUserQuery,
        fetchPolicy: "network-only"
      });
      const currentUser = data?.currentUser;
      //@ts-expect-error Testing for a global property
      const Sentry = window.Sentry;
      Sentry?.configureScope(scope => {
        currentUser && scope.setUser({ id: currentUser.uid });
      });

      /*
       * identify is first called here on page load for users who are logged in OR
       * immediately after a user login.
       *
       * For logged-out users see routes/AppContainer.tsx.
       */
      this._identify?.(currentUser?.uid, currentUser, undefined, () => {
        if (currentUser) {
          updateAttributionForCurrentUser(this._analytics, currentUser);

          /*
           * If the user has selected DNSMPI in the past as indicated by
           * their user data, but the preference is not set locally in ad
           * settings, update ad settings and reload the page to unload
           * any ad related integrations from their anonymous browsing
           * session.
           */
          if (
            !!this.localStorage &&
            currentUser.do_not_sell_my_personal_info &&
            !this._adSettings.shouldNotSellPersonalInfo
          ) {
            this._adSettings.updateDoNotSellMyPersonalInfo(true);
            this._trackEvent("dnsmpi_mismatch", undefined, undefined, () => {
              window.location.reload();
            });
          }
        }
      });

      if (currentUser) {
        this._updateUserInterfacePreferencesFromUser?.(currentUser);
      }
      return currentUser;
    } finally {
      this._currentUserHasLoaded = true;
      this._currentUserIsLoading = false;
      this.emitChange();
    }
  }

  async _updateCurrentUser(property, value) {
    await this._refreshCurrentUser();
    if (property) {
      const payload = value ? { property, value } : { property };
      this._trackEvent?.(ActionType.User.UPDATE, payload);
      this._trackEvent?.("Updated preference", payload, {
        integrations: {
          [Integration.Intercom]: true
        }
      });
    }
  }

  get currentUserIsLoading() {
    return !!this._currentUserIsLoading;
  }

  get currentUserHasLoaded() {
    return !!this._currentUserHasLoaded;
  }

  _loginCurrentUser(sessionToken, refreshToken?) {
    this._setTokens(sessionToken, refreshToken);
  }

  requireAuth = location => {
    if (!this._getAuthData().isLoggedIn) {
      this[ActionType.Webapp.ENTER_LOGIN_FLOW]({
        postLoginPath: location.pathname + location.search
      });
      return Routes.loginPath();
    }
    return undefined;
  };

  getEnrollPath = location => {
    if (!this._getAuthData().isLoggedIn) {
      const fullPath = location.pathname + location.search;
      this[ActionType.Webapp.ENTER_LOGIN_FLOW]({
        // NotEnrolledView component triggers a redirect with the additional URL params, we want to get rid of them post login
        postLoginPath: removeParamsFromUrl(fullPath, ["authTrigger", "signup"])
      });
      return addParamsToUrl(fullPath, {
        authTrigger: AuthTrigger.ENROLL,
        signup: true,
        skipLearners: true
      });
    }
    return undefined;
  };

  requireAdmin = () => {
    if (!this._getAuthData().isAdmin) {
      return browseRootPath();
    }
    return undefined;
  };

  requireNonLoggedIn = () => {
    if (this._getAuthData().isLoggedIn) {
      return Routes.parentSettingsPath();
    }
    return undefined;
  };

  requireRole = (_location, roles) => {
    if (!requireRoles(this._getTokens().decodedToken, roles, true)) {
      return browseRootPath();
    }
    return undefined;
  };

  requireExplicitRole = (_location, roles) => {
    if (!requireRoles(this._getTokens().decodedToken, roles, false)) {
      return browseRootPath();
    }
    return undefined;
  };

  requireAdminForOrganization = (
    organizationTypes,
    loggedOutRedirectRoute?
  ) => {
    const { adminForOrganizationType, isLoggedIn } = this._getAuthData();
    if (loggedOutRedirectRoute && !isLoggedIn) {
      return loggedOutRedirectRoute;
    } else if (!organizationTypes.includes(adminForOrganizationType)) {
      return browseRootPath();
    }
    return undefined;
  };

  requireAdminForSchool = () => {
    this.requireAdminForOrganization(
      [OrganizationType.CharterSchool, OrganizationType.School],
      outschoolForSchoolsUrl()
    );
  };

  requireAdminForCompany = () => {
    this.requireAdminForOrganization(
      [OrganizationType.Company],
      Routes.outschoolForEmployersUrl()
    );
  };

  requireAdminForPartner = () => {
    this.requireAdminForOrganization([OrganizationType.CommunityPartner]);
  };
}
